/**
 * HealthFigure — the organization's open health issues: one dot per issue in the color of its
 * severity, or a check when there is none.
 */

import { CircleCheck } from 'lucide-react';
import type { ReactElement } from 'react';
import React from 'react';

import { cn } from '@/lib/utils';
import type { HealthInfo, LauncherFigurePart } from '@/types/panels';
import { LauncherTileSize } from '@/types/panels';

import { FigureLegend } from './FigureLegend';
import { getCountedNoun } from './launcherInfo';
import { StatusDots } from './StatusDots';
import { TileFigure } from './TileFigure';

interface Props {
    info: HealthInfo;
    size: LauncherTileSize;
}

export const HealthFigure = ({ info, size }: Props): ReactElement => {
    const parts: LauncherFigurePart[] = [
        {
            label: getCountedNoun(info.errors, 'error'),
            count: info.errors,
            fill: 'bg-occ-red-1000',
        },
        {
            label: getCountedNoun(info.warnings, 'warning'),
            count: info.warnings,
            fill: 'bg-occ-yellow-1000',
        },
        {
            label: getCountedNoun(info.notices, 'notice'),
            count: info.notices,
            fill: 'bg-occ-brand',
        },
    ];
    const total = info.errors + info.warnings + info.notices;
    const isSmall = size === LauncherTileSize.Small;
    const check = cn('shrink-0 text-occ-green-1000', isSmall ? 'size-4' : 'size-5');
    const graphic = total === 0
        ? (
            <span className="flex items-center gap-1.5 text-sm font-medium">
                <CircleCheck className={check} />
                {!isSmall && 'All clear'}
            </span>
        )
        : (
            <StatusDots
                parts={parts}
                size={size}
            />
        );
    return (
        <TileFigure
            size={size}
            value={String(total)}
            unit={`open ${getCountedNoun(total, 'issue')}`}
            graphic={graphic}
            legend={total === 0 ? undefined : <FigureLegend parts={parts} />}
            brief={total === 0 ? 'All clear' : `${total} ${getCountedNoun(total, 'issue')}`}
        />
    );
};
